import {connect} from 'react-redux';

import {DraftMessageModel} from '../../../../libs/shared/core/src/models/api/messageModel';
import {reportToBugsnag} from '../../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {CellsContext} from '../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {
  ApplicationsPanelId,
  ViewerPanelId,
} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {
  createSelector,
  makeMapStateToProps,
  wrapInDeepEqualSelector,
  wrapInShallowSelector,
} from '../../../../srcCommon/helpers/reselectHelpers';
import {serializeSingleConversationForApplication} from '../../../helpers/models/applicationSerializationHelpers';
import {makeConversationActionContext} from '../../../helpers/models/conversationHelpers';
import {ApplicationEntryPointModel} from '../../../models/api/applicationEntryPointModel';
import {AppState} from '../../../store/appState';
import {serializeDraftForApplication} from '../../../store/data/applications/applicationsMessagesActions';
import {getCustomFieldsForCardsTable} from '../../../store/data/customFields/customFieldsSelectors';
import {getCardsTable, getContacts, getConversations} from '../../../store/data/dataSelectors';
import {getInboxesTable} from '../../../store/data/inboxes/inboxesSelectors';
import {getTagsTable} from '../../../store/data/tags/tagsSelectors';
import {
  getFindTeammate,
  getFindTeammateByEmail,
  getUserId,
} from '../../../store/data/teammates/teammatesSelectors';
import {getTopicsTable} from '../../../store/data/topics/topicsSelectors';
import {MessageComposerApplicationContextHost} from './messageComposerApplicationContextHost';

/*
 * Props.
 */

interface MessageComposerApplicationContextHostContainerProps {
  applicationsPanelId: ApplicationsPanelId;
  draftMessage: DraftMessageModel;
  entryPoint: ApplicationEntryPointModel;
  cellsContext: CellsContext | undefined;
  viewerPanelId: ViewerPanelId | undefined;
}

/*
 * Selectors.
 */

function getDraftMessage(_state: AppState, props: MessageComposerApplicationContextHostContainerProps) {
  return props.draftMessage;
}

function getCellsContext(_state: AppState, props: MessageComposerApplicationContextHostContainerProps) {
  return props.cellsContext;
}

function getViewerPanelId(_state: AppState, props: MessageComposerApplicationContextHostContainerProps) {
  return props.viewerPanelId;
}

const getDraftConversation = createSelector(
  [getDraftMessage, getConversations, getViewerPanelId],
  (draft, conversations, viewerPanelId) => {
    if (!viewerPanelId) {
      // If we don't have the correct viewerPanelId which contains the
      // conversation, it means that the conversation is not selected.
      return undefined;
    }
    return conversations[draft.conversationId];
  },
);

const getConversationId = createSelector([getDraftConversation], (conversation) => {
  if (!conversation) {
    return undefined;
  }
  return conversation.id;
});

function getState(state: AppState, props: MessageComposerApplicationContextHostContainerProps) {
  return state;
}

/*
 * Single conversation selectors.
 */

function makeGetApplicationSingleConversation() {
  return createSelector(
    [
      getFindTeammate,
      getFindTeammateByEmail,
      getInboxesTable,
      getTagsTable,
      getContacts,
      getCardsTable,
      getCustomFieldsForCardsTable,
      getUserId,
      getDraftConversation,
      getDraftMessage,
      getTopicsTable,
    ],
    (
      findTeammate,
      findTeammateByEmail,
      inboxes,
      tags,
      contacts,
      cards,
      customFields,
      userId,
      conversation,
      draftMessage,
      topics,
    ) => {
      if (!conversation) {
        return undefined;
      }

      try {
        const data = {
          findTeammate,
          findTeammateByEmail,
          inboxes,
          tags,
          contacts,
          cards,
          customFields,
          topics,
        };
        return serializeSingleConversationForApplication(data, userId, conversation, draftMessage.uid);
      } catch (error) {
        reportToBugsnag(error);
        return undefined;
      }
    },
  );
}

function makeGetApplicationDraft() {
  return createSelector([getDraftMessage, getState], (draft, state) =>
    serializeDraftForApplication(state, draft),
  );
}

function makeGetConversationActionContext() {
  return createSelector(
    [getCellsContext, getDraftConversation],
    (cellsContext, conversation) => () =>
      conversation && cellsContext && makeConversationActionContext(cellsContext, conversation),
  );
}

function makeGetConversationNamespace() {
  return createSelector(
    [getDraftConversation],
    (conversation) => () => conversation && conversation.namespace,
  );
}

function makeGetConversationInboxIds() {
  return createSelector(
    [getDraftConversation],
    (conversation) => () => conversation && conversation.inboxIds,
  );
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<MessageComposerApplicationContextHostContainerProps>()(() => {
  const getApplicationSingleConversation = makeGetApplicationSingleConversation();
  const getConversationNamespace = makeGetConversationNamespace();
  const getConversationActionContext = wrapInShallowSelector(makeGetConversationActionContext());
  const getConversationInboxIds = wrapInDeepEqualSelector(makeGetConversationInboxIds());

  return {
    findConversationActionContext: getConversationActionContext,
    findConversationNamespace: getConversationNamespace,
    findConversationInboxIds: getConversationInboxIds,
    conversation: wrapInDeepEqualSelector(getApplicationSingleConversation),
    applicationDraft: makeGetApplicationDraft(),
    conversationId: getConversationId,
  };
});

export const MessageComposerApplicationContextHostContainer = connect(mapStateToProps)(
  MessageComposerApplicationContextHost,
);
